import React, { useEffect, useState } from 'react';
import './App.css';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import Container from 'saur_ui_library/src/Atoms/Container/Container';
import Menu from './components/Menus/Menu';
import NotFound from './components/common/NotFound';
import DoTpDetail from './Views/PrDoDetail/DoTpDetail';
import PrMrDetail from './Views/PrDoDetail/PrMrDetail';
import ContractParameterView from './Views/ContractParameterView';
import H2sParam from './Views/component/ParamComponent';
import { LocalStorage } from './utils/localStorage';
import MonitoringView from './Views/MonitoringView';
import { HttpArcgis } from './utils/http';
import LoginPopup from './components/login/LoginPopup';
import AuthenticationWrapper from './components/login/login-widget/login/AuthenticationWrapper';
import { TokensType } from './components/login/common/authProvider';
import ViewComponent from './components/module/ViewComponent';
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './ApplicationInsights/appInsights';
import CollectionPoolView from './Views/collectionPool/CollectionPoolView';
import CollectionPoolDetail from './Views/collectionPool/CollectionPoolDetail';
import AdministrationView from './Views/administration/AdministrationView';
import { app_placeholder } from './env.placeholders';

function App() {
  const roles = LocalStorage.userRole.get();
  const hasContract = !!window.sessionStorage.getItem('contractCode');
  const [fetched, SetFetched] = useState(false);
  const [showHeader, setShowHeader] = useState<boolean>(false);

  const onToken = (
    token: TokensType,
    expireDate: number | undefined,
    pRoles: string[] | undefined,
    email: string,
  ) => {
    LocalStorage.token.set(token.token);
    LocalStorage.refreshToken.set(token.refreshToken);
    LocalStorage.userRole.set(JSON.stringify(pRoles));
    setShowHeader(true);
  };

  useEffect(() => {
    const token = LocalStorage.token.get();
    setShowHeader(!!token);
  }, []);

  if (!fetched && showHeader) {
    // get legend for all services
    HttpArcgis.get(
      app_placeholder.VITE_APP_ARCGIS_SERVER + '?path=legend?f=json',
    ).then((res: any) => {
      const legend = res.data;
      const data = legend.layers;      
      window.localStorage.setItem('legend', JSON.stringify(data));
    });
    SetFetched(true);
  }

  const errorMessage = () => <h1>Une erreur est survenue</h1>;

  // component for routes
  const doComponent = () => <ViewComponent module={'do'} />;
  const prComponent = () => <ViewComponent module={'pr'} />;
  const ecpComponent = () => <ViewComponent module={'ecp'} />;
  const boonListerComponent = () => (
    <ViewComponent module={'h2s'} option={'IndicatorBoonLister'} />
  );
  const residenceTimePipelinesComponent = () => (
    <ViewComponent module={'h2s'} option={'IndicatorSejourCana'} />
  );
  const residenceTimeComponent = () => <ViewComponent module={'h2s'} option={'IndicatorSejourPR'} />;
  const selfCleaningFittingComponent = () => (
    <ViewComponent module={'h2s'} option={'IndicatorAutocurage'} />
  );
  const groundComponent = () => (
    <ViewComponent module={'h2s'} option={'IndicatorTerrain'} />
  );
  const indusComponent = () => <ViewComponent module={'indus'} />;
  const hydroComponent = () => <ViewComponent module={'hydro'} />;
  const sensibilityComponent = () => (
    <ViewComponent module={'milieu'} option={'sensibility'} />
  );
  const dilutionComponent = () => (
    <ViewComponent module={'milieu'} option={'dilution'} />
  );
  const pluieComponent = () => <ViewComponent module={'pluie'} />;

  // elaboration des roles:
  /*
  - Profil Consultation : visualisation et export de données (tous les profils internes). Les modules auxquels l'utilisateurs aura droit sont :
      * Visualisation de PR/DO
      * Export de données 
      * Visualisation PR de l'outil H2S
      * Restitution des indicateurs pour l'outil H2S
  -  Profil Edition : consultation + modification (paramétrage, import, traitement)
      * meme module que profil consultation
      * Paramétrage de données
      * Traitement de données 
  */
  return (
    <AuthenticationWrapper
      lang='fr'
      realm={app_placeholder.VITE_APP_REALM_KEYCLOAK ?? ''}
      clientIdExtern='diagpermweb-client'
      clientIdIntern='DiagPermWeb'
      idpUrl={app_placeholder.VITE_APP_URL_KEYCLOAK ?? ''}
      onToken={onToken}
    >
      <AppInsightsErrorBoundary
        onError={errorMessage}
        appInsights={reactPlugin}
      >
          <HashRouter>
            <Container className='sds-reset-style color-nav' id='header'>
              {showHeader && <Menu />}
            </Container>
            <Container>{!showHeader && <LoginPopup />}</Container>
            <Container className='body' id='body'>
              <Routes>
                <Route
                  path='/'
                  // only redirect once a contract is already known (returning session);
                  // otherwise stay on '/' so no module view mounts before selection
                  element={hasContract ? <Navigate to='/DO' replace /> : null}
                />
                <Route path='/DO' element={doComponent()} />
                <Route path='/PR' element={prComponent()} />
                <Route path='/ECP' element={ecpComponent()} />
                <Route
                  path='/IndicatorBoonLister'
                  element={boonListerComponent()}
                />
                <Route
                  path='/IndicatorSejourCana'
                  element={residenceTimePipelinesComponent()}
                />
                <Route path='/IndicatorSejourPR' element={residenceTimeComponent()} />
                <Route
                  path='/IndicatorAutocurage'
                  element={selfCleaningFittingComponent()}
                />
                <Route
                  path='/IndicatorTerrain'
                  element={groundComponent()}
                />
                <Route path='/Indus' element={indusComponent()} />
                <Route path='/Hydro' element={hydroComponent()} />
                <Route
                  path='/MilieuSensibility'
                  element={sensibilityComponent()}
                />
                <Route
                  path='/MilieuDilution'
                  element={dilutionComponent()}
                />
                <Route path='/Pluie' element={pluieComponent()} />
                {roles?.includes('diagperm-admin') ? (
                  <Route path='/Monitoring' element={<MonitoringView />} />
                ) : (
                  <Route path='/H2SParametres' element={<NotFound />} />
                )}
                <Route path='/DODetaille' element={<DoTpDetail />} />
                <Route path='/PRDetaille' element={<PrMrDetail />} />
                <Route
                  path='/CollectionPoolDetaille'
                  element={<CollectionPoolDetail />}
                />

                {roles?.includes('diagperm-admin') ||
                roles?.includes('diagperm-edit') ? (
                  <Route
                    path='/Parametre'
                    element={<ContractParameterView />}
                  />
                ) : (
                  <Route path='/Parametre' element={<NotFound />} />
                )}

                {roles?.includes('diagperm-admin') ||
                roles?.includes('diagperm-edit') ? (
                  <Route path='/H2SParametres' element={<H2sParam IsVisible={false} title={''} mode={''} />} />
                ) : (
                  <Route path='/H2SParametres' element={<NotFound />} />
                )}

                <Route
                  path='/CollectionPool'
                  element={<CollectionPoolView />}
                />

                {roles?.includes('diagperm-admin') && (
                  <Route
                    path='/Administration'
                    element={<AdministrationView />}
                  />
                )}
              </Routes>
            </Container>
          </HashRouter>
      </AppInsightsErrorBoundary>
    </AuthenticationWrapper>
  );
}

export default App;
